import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Button, showAlert } from '@/components';
import Loader from '../../components/common/loader/Loader';
import { FiChevronDown, FiChevronRight, FiCheck, FiX } from 'react-icons/fi';
import { getProductSyncRequests, getClassListForProposal, getBtsProductsByClass, bulkActionProductSyncRequest } from '@/services/BtsService';
import Swal from 'sweetalert2';

const checkIsHeader = (req, product) => {
    if (product?.isHeaderRow) return true;
    if (req?.requestType === 'ADDED_NEW_PRODUCT' && (product?.headerType || req?.fieldName === 'header' || req?.newValue === 'INLINE_NOTE' || req?.newValue === 'SECTION_HEADER' || req?.newValue === 'CHOOSE_ONE_GROUP')) return true;
    if (req?.fieldName === 'header' || req?.fieldName === 'headerType' || req?.fieldName === 'sequenceNumber' || req?.fieldName === 'sequence_number') {
        const itemCode = product?.originalItemCode !== undefined ? product.originalItemCode : product?.itemCode;
        if (!itemCode || itemCode.trim() === '') return true;
    }
    const itemCode = product?.originalItemCode !== undefined ? product.originalItemCode : product?.itemCode;
    if (!itemCode || itemCode.trim() === '') return true;
    return false;
};

const formatFieldName = (req, product) => {
    const fieldName = req.fieldName;
    const requestType = req.requestType;
    const isHeader = checkIsHeader(req, product);
    
    if (requestType === 'ADDED_NEW_PRODUCT') {
        return isHeader ? 'Add New Header' : 'Add New Product';
    }
    if (requestType === 'DELETE_PRODUCT' || (fieldName === 'status' && req.newValue === 'Deleted')) {
        return isHeader ? 'Delete Header' : 'Delete Product';
    }
    if (fieldName === 'sequenceNumber' || fieldName === 'sequence_number') {
        return isHeader ? 'Header Position' : 'Sequence Number';
    }
    if (fieldName === 'header') {
        return 'Header Title';
    }
    if (fieldName === 'headerType') {
        return 'Header Type';
    }
    return (fieldName || requestType || '').replace(/_/g, ' ');
};

const formatPreviousState = (req, product) => {
    const fieldName = req.fieldName;
    const requestType = req.requestType;
    const val = req.oldValue;
    if (val === undefined || val === null || val === '') return '—';
    const stringVal = String(val);

    const isHeader = checkIsHeader(req, product);

    if ((fieldName === 'sequenceNumber' || fieldName === 'sequence_number') && isHeader) {
        return 'Original Position';
    }
    return stringVal;
};

const formatNewState = (req, product) => {
    const fieldName = req.fieldName;
    const requestType = req.requestType;
    const val = req.newValue;
    if (val === undefined || val === null || val === '') return '—';
    const stringVal = String(val);

    const isHeader = checkIsHeader(req, product);

    if (requestType === 'ADDED_NEW_PRODUCT' && isHeader) {
        const headerTitle = product?.header || (stringVal !== 'SECTION_HEADER' && stringVal !== 'INLINE_NOTE' && stringVal !== 'CHOOSE_ONE_GROUP' ? stringVal : 'New Header');
        if (product && product.belowProductDescription) {
            return `"${headerTitle}" (above ${product.belowProductDescription})`;
        }
        return `"${headerTitle}"`;
    }

    if ((fieldName === 'sequenceNumber' || fieldName === 'sequence_number') && isHeader) {
        if (product && product.belowProductDescription) {
            return `Header moved above ${product.belowProductDescription}`;
        }
        return `Position ${stringVal}`;
    }

    if (fieldName === 'headerType') {
        if (stringVal === 'SECTION_HEADER') return 'Section Header';
        if (stringVal === 'INLINE_NOTE') return 'Inline Note';
        if (stringVal === 'CHOOSE_ONE_GROUP') return 'Choose One Group';
        return stringVal;
    }

    if (fieldName === 'productType') {
        return stringVal === 'true' ? 'Required' : 'Optional';
    }

    if ((fieldName === 'status' || requestType === 'DELETE_PRODUCT') && stringVal === 'Deleted') {
        return isHeader ? 'Header deleted' : 'Product deleted';
    }

    if (stringVal === 'true') return 'Yes';
    if (stringVal === 'false') return 'No';

    return stringVal;
};

const ReviewChanges = () => {
    const { id: proposalId } = useParams();
    const [loading, setLoading] = useState(true);
    const [classList, setClassList] = useState([]);
    const [changesMap, setChangesMap] = useState({}); // ClassID -> { ProductID -> [Changes] }
    const [expandedClasses, setExpandedClasses] = useState({});

    // Keep track of decisions: requestId -> 'ACCEPT' | 'REJECT'
    const [decisions, setDecisions] = useState({});

    useEffect(() => {
        fetchData();
    }, [proposalId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [classes, requests] = await Promise.all([
                getClassListForProposal(proposalId),
                getProductSyncRequests(proposalId)
            ]);
            setClassList(classes);
            
            // 1. Fetch products for all classes
            const productPromises = classes.map(cls => 
                getBtsProductsByClass(proposalId, cls.id).then(products => ({ classId: cls.id, products }))
            );
            
            const results = await Promise.all(productPromises);
            const productMap = {}; 
            results.forEach(res => {
                 if(Array.isArray(res.products)) {
                     res.products.forEach(p => productMap[p.id] = p);
                 }
            });

            // 2. Enrich and Group Requests
            const enrichedRequests = requests.map(req => {
                let product = productMap[req.schoolProductId];

                // If product is not in active productMap (e.g. ADDED_NEW_PRODUCT draft header), create fallback display object
                if (!product) {
                    product = {
                        id: req.schoolProductId,
                        itemCode: '',
                        description: req.newValue || 'New Header / Item',
                        header: req.newValue || '',
                        headerType: req.requestType === 'ADDED_NEW_PRODUCT' ? 'SECTION_HEADER' : null,
                        btsClassId: req.btsClassId || (classList[0] ? classList[0].id : null)
                    };
                }

                const isHeader = (product.headerType || req.requestType === 'ADDED_NEW_PRODUCT' || req.fieldName === 'headerType' || req.fieldName === 'header' || req.fieldName === 'sequenceNumber' || req.fieldName === 'sequence_number') && (!product.itemCode || product.itemCode.trim() === '');
                let displayProduct = { ...product, originalItemCode: product.itemCode, isHeaderRow: isHeader };
                if (isHeader) {
                    const classRes = results.find(r => r.classId === product.btsClassId);
                    if (classRes && Array.isArray(classRes.products)) {
                        const sorted = [...classRes.products].sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));
                        let seq = null;
                        if ((req.fieldName === 'sequenceNumber' || req.fieldName === 'sequence_number') && req.newValue) {
                            seq = parseInt(req.newValue, 10);
                        } else {
                            seq = product.sequenceNumber !== undefined ? product.sequenceNumber : null;
                        }
                        let belowProduct = null;
                        if (seq !== null) {
                            belowProduct = sorted.find(p => (p.sequenceNumber || 0) > seq && p.itemCode && p.itemCode.trim() !== '');
                        }
                        if (!belowProduct) {
                            const idx = sorted.findIndex(p => p.id === product.id);
                            if (idx !== -1) {
                                belowProduct = sorted.slice(idx + 1).find(p => p.itemCode && p.itemCode.trim() !== '');
                            }
                        }
                        if (belowProduct) {
                            displayProduct.belowProductDescription = belowProduct.description;
                        }
                    }
                }

                return { ...req, product: displayProduct, className: product?.className || 'Class Changes' };
            }).filter(Boolean); 

            const groupedByClass = {};
            
            enrichedRequests.forEach(req => {
                 const clsId = req.product.btsClassId; 
                 if (!groupedByClass[clsId]) {
                     groupedByClass[clsId] = {}; // Map of ProductID -> Changes
                 }
                 
                 const prodId = req.schoolProductId;
                 if (!groupedByClass[clsId][prodId]) {
                     groupedByClass[clsId][prodId] = [];
                 }
                 
                 // Logic to keep only LATEST change per field per product
                 // We'll append all first, then filter inline or pre-filter
                 // Simpler: Just accumulate all, then clean up.
                 groupedByClass[clsId][prodId].push(req);
            });

            // 3. Filter Latest Changes
            Object.keys(groupedByClass).forEach(clsId => {
                 const prodMap = groupedByClass[clsId];
                 Object.keys(prodMap).forEach(prodId => {
                     const reqs = prodMap[prodId];
                     const latestMap = {};
                     reqs.forEach(req => {
                         const key = `${req.fieldName || req.requestType}`; 
                         // Compare IDs to find the latest request
                         
                         const existing = latestMap[key];
                         if (!existing || req.id > existing.id) {
                             latestMap[key] = req;
                         }
                     });
                     prodMap[prodId] = Object.values(latestMap).sort((a,b) => b.id - a.id);
                 });
            });

            setChangesMap(groupedByClass);
            
            // Expand all by default
            const expanded = {};
            classes.forEach(c => expanded[c.id] = true);
            setExpandedClasses(expanded);

        } catch (err) {
            console.error(err);
            showAlert('error', 'Failed to load review data');
        } finally {
            setLoading(false);
        }
    };

    const parseDate = (dateVal) => {
        if (!dateVal) return null;
        if (Array.isArray(dateVal)) {
            // [yyyy, MM, dd, HH, mm, ss]
            return new Date(dateVal[0], dateVal[1] - 1, dateVal[2], dateVal[3] || 0, dateVal[4] || 0, dateVal[5] || 0);
        }
        return new Date(dateVal);
    };

    const formatDate = (dateVal) => {
         const d = parseDate(dateVal);
         if (!d) return '-';
         // Format YYYY-MM-DD
         return d.toISOString().split('T')[0];
    };

    const toggleClassExpand = (classId) => {
        setExpandedClasses(prev => ({...prev, [classId]: !prev[classId]}));
    };

    const handleDecision = (requestId, action) => {
        setDecisions(prev => {
            const current = prev[requestId];
            if (current === action) {
                // Toggle off if clicking same action
                const copy = { ...prev };
                delete copy[requestId];
                return copy;
            }
            return {
                ...prev,
                [requestId]: action
            };
        });
    };

    const [totalPendingCount, setTotalPendingCount] = useState(0);

    useEffect(() => {
        // Calculate total pending requests from the changesMap
        let pending = 0;
        Object.values(changesMap).forEach(classGroup => {
            Object.values(classGroup).forEach(reqs => {
                reqs.forEach(req => {
                    if (req.status === 'PENDING') pending++;
                });
            });
        });
        setTotalPendingCount(pending);
    }, [changesMap]);

     const handleSubmitToSchool = async () => {
          // Show confirmation dialog before submitting
         const result = await Swal.fire({
             title: 'Submit to School',
             text: 'Are you sure , you want to submit these changes to the school?',
             showCancelButton: true,
             confirmButtonText: 'Submit to School',
             cancelButtonText: 'Cancel',
             buttonsStyling: false,
             customClass: {
                 popup: 'rounded-xl',
                 title: 'font-bold text-xl mb-2',
                 htmlContainer: 'text-gray-600',
                 confirmButton: 'bg-[#ffb800] hover:bg-[#f1ad02] text-black text-lg font-normal py-2 px-6 rounded-3xl ml-4',
                 cancelButton: 'bg-[#e8e9ed] hover:bg-[#d9dade] text-black text-lg font-normal py-2 px-6 rounded-3xl'
             }
         });

         // If user cancelled, return
         if (!result.isConfirmed) {
             return;
         }

         // Group decisions by reviewId
         const actionsByReview = {};
         
         Object.entries(decisions).map(([id, action]) => {
             // Find the request object to get reviewId
             // We need to search in changesMap or we need a flat lookup.
             // Efficient way: store reviewId in decisions or look it up.
             // Let's look it up from changesMap.
             let reviewId = null;
             // Flat search (not optimal but safe)
             Object.values(changesMap).forEach(classGroup => {
                Object.values(classGroup).forEach(reqs => {
                    const found = reqs.find(r => r.id === parseInt(id));
                    if (found) reviewId = found.reviewId;
                });
             });
             
             if (reviewId) {
                 if (!actionsByReview[reviewId]) actionsByReview[reviewId] = [];
                 actionsByReview[reviewId].push({ requestId: parseInt(id), action });
             }
         });

         if (Object.keys(actionsByReview).length === 0) {
             showAlert('info', 'No changes selected to submit');
             return;
         }

         try {
             // Execute bulk actions in a single request
             const payload = Object.entries(actionsByReview).map(([reviewId, actions]) => ({
                 reviewId: parseInt(reviewId),
                 actions: actions
             }));
             
             await bulkActionProductSyncRequest(payload);
             
             setDecisions({}); 
             fetchData(); 
             
         } catch (err) {
             console.error(err);
             showAlert('error', 'Failed to submit changes');
         }
     };

    const getStatusForRequest = (req) => {
        if (decisions[req.id]) return decisions[req.id]; 
        return req.status; 
    };

    if (loading) return <Loader />;

    return (
        <Container title="BTS Proposal">
             <div className="mb-4">
                <Link
                    to="/bts"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-150 group"
                >
                    <svg
                        className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform duration-150"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    <span>Back to BTS</span>
                </Link>
             </div>

             <div className="bg-white rounded-lg shadow-sm p-4 min-h-[500px]">
                {classList.map(cls => {
                    const productMap = changesMap[cls.id] || {};
                    const productIds = Object.keys(productMap);
                    
                    if (productIds.length === 0) return null;

                    // Calculate total changes for badge
                    const totalChanges = productIds.reduce((acc, pid) => acc + productMap[pid].length, 0);

                    return (
                        <div key={cls.id} className="mb-6">
                            {/* Accordion Header */}
                            <div 
                                className="bg-blue-50 p-4 flex justify-between items-center cursor-pointer select-none rounded-t-lg border border-blue-100"
                                onClick={() => toggleClassExpand(cls.id)}
                            >
                                <div className="flex items-center">
                                    <span className="font-bold text-lg mr-3 text-gray-800">{cls.className}</span>
                                    <span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                                        {totalChanges} changes
                                    </span>
                                </div>
                                <div className="text-gray-500">
                                    {expandedClasses[cls.id] ? <FiChevronDown size={24} /> : <FiChevronRight size={24} />}
                                </div>
                            </div>
                            
                            {/* Accordion Body */}
                            {expandedClasses[cls.id] && (
                                <div className="border border-t-0 border-gray-200 rounded-b-lg p-0">
                                    {productIds.map(prodId => {
                                        const reqs = productMap[prodId];
                                        const product = reqs[0].product; 
                                        
                                        return (
                                            <div key={prodId} className="flex border-b border-gray-200 last:border-b-0">
                                                {/* Left: Product Info */}
                                                <div className="w-1/4 p-4 border-r border-gray-100 bg-gray-50/50 flex flex-col justify-center">
                                                    <div className="flex items-start">
                                                        <div>
                                                            <div className="font-bold text-gray-900 text-lg mb-1">{product.itemCode}</div>
                                                            <div className="text-sm text-gray-600 leading-relaxed">{product.description}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Changes List */}
                                                <div className="w-3/4 p-4">
                                                    <div className="grid grid-cols-[2fr,2fr,2fr,4.5fr,2.5fr,1.5fr,1.5fr,1.5fr] gap-4 text-xs font-semibold text-gray-500 uppercase mb-2 border-b border-gray-100 pb-2">
                                                        <div>Change Field</div>
                                                        <div>Previous State</div>
                                                        <div>New State</div>
                                                        <div>Requested By</div>
                                                        <div>Approved By</div>
                                                        <div className="text-center">Action</div>
                                                        <div className="text-center">Status</div>
                                                        <div className="text-right">Date</div>
                                                    </div>
                                                    
                                                    {reqs.map(req => {
                                                        const currentStatus = getStatusForRequest(req);
                                                        
                                                        let statusBadge;
                                                        if (currentStatus === 'PENDING') statusBadge = <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">Pending</span>;
                                                        else if (currentStatus === 'APPROVED' || currentStatus === 'ACCEPT') statusBadge = <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Approved</span>;
                                                        else if (currentStatus === 'REJECTED' || currentStatus === 'REJECT') statusBadge = <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Rejected</span>;

                                                        return (
                                                            <div key={req.id} className="grid grid-cols-[2fr,2fr,2fr,4.5fr,2.5fr,1.5fr,1.5fr,1.5fr] gap-4 items-center mb-3 last:mb-0">
                                                                <div className="font-medium text-gray-700 capitalize">
                                                                    {formatFieldName(req, product)}
                                                                </div>
                                                                <div className="text-gray-500 break-words">
                                                                    {formatPreviousState(req, product)}
                                                                </div>
                                                                <div className="font-bold text-gray-900 break-words">
                                                                    {formatNewState(req, product)}
                                                                </div>
                                                                <div className="text-sm text-gray-600 truncate" title={req.requestedByEmail || ''}>
                                                                    {req.requestedByEmail || '—'}
                                                                </div>
                                                                <div className="text-sm text-gray-600 truncate" title={req.reviewedBy || ''}>
                                                                    {req.status === 'APPROVED' || req.status === 'REJECTED' ? req.reviewedBy || '—' : '—'}
                                                                </div>
                                                                <div className="flex justify-center space-x-2">
                                                                    <button 
                                                                        onClick={() => handleDecision(req.id, 'ACCEPT')}
                                                                        disabled={currentStatus !== 'PENDING' && currentStatus !== 'ACCEPT' && currentStatus !== 'REJECT'}
                                                                        className={`w-10 h-10 flex items-center justify-center rounded-lg border-2 transition-all duration-200 
                                                                            ${(currentStatus === 'ACCEPT' || currentStatus === 'APPROVED') 
                                                                                ? 'bg-green-50 border-green-500 text-green-600' 
                                                                                : 'bg-white border-gray-200 text-gray-400 hover:border-green-300 hover:text-green-500 hover:scale-105'}`}
                                                                    >
                                                                        <FiCheck size={20} strokeWidth={3} />
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => handleDecision(req.id, 'REJECT')}
                                                                        disabled={currentStatus !== 'PENDING' && currentStatus !== 'ACCEPT' && currentStatus !== 'REJECT'}
                                                                        className={`w-10 h-10 flex items-center justify-center rounded-lg border-2 transition-all duration-200 
                                                                            ${(currentStatus === 'REJECT' || currentStatus === 'REJECTED') 
                                                                                ? 'bg-red-50 border-red-500 text-red-600' 
                                                                                : 'bg-white border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-500 hover:scale-105'}`}
                                                                    >
                                                                        <FiX size={20} strokeWidth={3} />
                                                                    </button>
                                                                </div>
                                                                
                                                                <div className="text-center">
                                                                    {statusBadge}
                                                                </div>
                                                                
                                                                <div className="text-right text-sm text-gray-500">
                                                                    {formatDate(req.updatedDate || req.createdDate)}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
                
                <div className="flex justify-end mt-8">
                    <div className="relative group inline-block">
                        <Button 
                            onClick={handleSubmitToSchool} 
                            disabled={Object.values(decisions).length === 0}
                            className={Object.values(decisions).length === 0 ? 'btn-disabled' : 'primary'}
                        >
                            Submit to School
                        </Button>
                    </div>
                </div>
             </div>
        </Container>
    );
};

export default ReviewChanges;
